import Phaser from 'phaser';
import { SCENE_KEYS, ZONE_NAMES } from '../data/gameConstants';
import { Collectible } from '../objects/Collectible';
import { InteractionZone } from '../objects/InteractionZone';
import { SaveSystem } from '../systems/SaveSystem';
import { ZoneCoreSystems } from '../systems/ZoneCoreSystems';
import { createEmergencyBagOverlay, EmergencyBagOverlay } from '../ui/EmergencyBagOverlay';
import { ObjectiveHint } from '../ui/ObjectiveHint';
import { QuestPanel } from '../ui/QuestPanel';
import { addZoneHudHeader } from '../ui/ZoneHudHeader';
import { addZoneMenuButton } from '../ui/zoneMenuButton';
import { SceneTransition } from '../utils/SceneTransition';
import { ZONE3_POSITIONS, Zone3Map } from '../zones/zone3/Zone3Map';
import { Zone3QuestFlow } from '../zones/zone3/Zone3QuestFlow';

export class Zone3BubunganHouseScene extends Phaser.Scene {
  private core?: ZoneCoreSystems;
  private questPanel?: QuestPanel;
  private questFlow?: Zone3QuestFlow;
  private map?: Zone3Map;
  private keris?: Collectible;
  private objectiveHint?: ObjectiveHint;
  private bagOverlay?: EmergencyBagOverlay;

  constructor() {
    super({ key: 'Zone3BubunganHouseScene' });
  }

  create(): void {
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);

    this.map = new Zone3Map(this);
    const obstacles = this.map.build();

    this.core = new ZoneCoreSystems(this);
    this.questPanel = new QuestPanel(this);
    this.objectiveHint = new ObjectiveHint(this);
    this.bagOverlay = createEmergencyBagOverlay();

    this.core.setQuestPanel(this.questPanel);
    this.core.setupHUD(3);
    this.core.setupMobileControls();
    const startPos = SaveSystem.getPlayerStartPosition(
      SCENE_KEYS.ZONE3,
      ZONE3_POSITIONS.SPAWN.x,
      ZONE3_POSITIONS.SPAWN.y,
    );
    SaveSystem.saveProgress(SCENE_KEYS.ZONE3, 3, startPos);
    this.core.initPlayer(startPos.x, startPos.y);
    this.core.setupObstacles(obstacles);

    this.keris = new Collectible(this, ZONE3_POSITIONS.KERIS.x, ZONE3_POSITIONS.KERIS.y, {
      id: 'zone3_keris2',
      label: 'Keris 2',
      type: 'keris_2',
      scoreRuleId: 'zone3_keris2',
      dialogueId: 'zone3_rafflesia_keris2',
      kerisId: 'keris_2',
      startHidden: true,
      interactionRadius: 80,
    });

    const signZone = new InteractionZone(
      this,
      ZONE3_POSITIONS.SIGN.x,
      ZONE3_POSITIONS.SIGN.y,
      70,
      60,
      {
        id: 'zone3_sign',
        type: 'custom',
        label: 'Baca Papan Informasi',
        interactionRadius: 85,
      },
    );

    const bagZone = new InteractionZone(
      this,
      ZONE3_POSITIONS.BAG_STATION.x,
      ZONE3_POSITIONS.BAG_STATION.y,
      100,
      60,
      {
        id: 'zone3_bag_station',
        type: 'custom',
        label: 'Siapkan Tas Darurat',
        interactionRadius: 95,
      },
    );

    this.questFlow = new Zone3QuestFlow(
      this.core,
      this.map,
      this.keris,
      this.objectiveHint,
      this.bagOverlay,
    );

    signZone.setOnInteract(() => this.questFlow!.onSignInteract());
    bagZone.setOnInteract(() => this.questFlow!.onBagStationInteract());
    this.keris.setOnCollect(() => this.questFlow!.onKerisCollected());

    this.core.registerInteractables([signZone, bagZone, this.keris]);
    this.buildUI();
    this.questFlow.start();
    SceneTransition.reveal(this);
  }

  private buildUI(): void {
    addZoneHudHeader(this, ZONE_NAMES.ZONE3, 'Persiapan di Rumah Bubungan Lima');
    addZoneMenuButton(this);
  }

  update(time: number, delta: number): void {
    if (this.bagOverlay?.isVisible()) return;

    this.core?.update(time, delta);
    this.keris?.updateGlow();

    const player = this.core?.getPlayer();
    if (player && this.questFlow) {
      this.questFlow.update(player.x, player.y);
    }
  }

  shutdown(): void {
    this.questFlow?.destroy();
    this.core?.destroy();
    this.questFlow = undefined;
    this.core = undefined;
    this.questPanel = undefined;
  }
}

